import type { HTMLAttributes, MouseEventHandler, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { cn } from '../../lib/utils';

const VARIANT_CLASSES: Record<string, string> = {
    primary:
        'bg-button-background text-button-foreground hover:bg-button-background-hover active:bg-button-background-active focus:outline-focus-primary disabled:bg-button-background-disabled',
    secondary:
        'bg-gray-200 hover:bg-gray-300 active:bg-gray-400 focus:outline-focus-primary text-black disabled:bg-gray-100',
    tertiary:
        'bg-violet-200 hover:bg-violet-300 active:bg-violet-400 focus:outline-focus-primary text-black disabled:bg-violet-100',
    outlined:
        'bg-white hover:bg-gray-100 active:bg-gray-200 focus:outline-focus-secondary text-black border border-solid border-gray-300 disabled:bg-gray-100',
    plain: 'bg-white hover:bg-gray-100 active:bg-gray-200 focus:outline-focus-secondary text-black',
    transparent: 'bg-transparent hover:bg-gray-100 active:bg-gray-200 focus:outline-focus-secondary text-black',
    danger: 'bg-red-500 hover:bg-red-600 active:bg-red-700 focus:outline-focus-primary text-white disabled:bg-red-100 disabled:text-white',
    info: 'bg-blue-500 hover:bg-blue-600 active:bg-blue-700 focus:outline-focus-primary text-white disabled:bg-blue-100 disabled:text-white',
    success:
        'bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 focus:outline-focus-primary text-white disabled:bg-emerald-100 disabled:text-white',
    warning:
        'bg-orange-500 hover:bg-orange-600 active:bg-orange-700 focus:outline-focus-primary text-white disabled:bg-orange-100 disabled:text-white',
};

export type CommonTypes = {
    variant?:
        | 'primary'
        | 'secondary'
        | 'tertiary'
        | 'outlined'
        | 'plain'
        | 'transparent'
        | 'danger'
        | 'info'
        | 'success'
        | 'warning'
        | string;
    small?: boolean;
    children?: ReactNode;
    iconStart?: ReactNode;
    iconEnd?: ReactNode;
    dataTestid?: string;
    className?: string;
};

export type ButtonTypes = CommonTypes & {
    type?: 'button';
    onClick: MouseEventHandler;
    disabled?: boolean;
    htmlAttributes?: HTMLAttributes<HTMLButtonElement>;
};

export type ButtonSubmitTypes = CommonTypes & {
    type?: 'submit';
    onClick?: MouseEventHandler;
    disabled?: boolean;
    htmlAttributes?: HTMLAttributes<HTMLButtonElement | HTMLFieldSetElement>;
};

export type LinkTypes = CommonTypes & {
    type?: 'link';
    to: string;
    htmlAttributes?: HTMLAttributes<HTMLAnchorElement>;
    disabled?: boolean;
};

export type ExternalLinkTypes = CommonTypes & {
    type?: 'external-link';
    href: string;
    htmlAttributes?: HTMLAttributes<HTMLAnchorElement>;
    disabled?: boolean;
};

export type ButtonProps = ButtonTypes | ButtonSubmitTypes | LinkTypes | ExternalLinkTypes;

export const Button = (props: ButtonProps) => {
    // Note : I did used props destructuration to work around a type error on `onClick`, `to` and `href` props, not defined depending on `type`

    const variant = props.variant ?? 'primary';
    const type = props.type ?? 'button';
    const small = props.small ?? false;
    const classNames = cn(
        'text-sm rounded-md gap-2 flex whitespace-nowrap items-center cursor-pointer font-normal',
        'focus:outline-solid focus:outline-2',
        'disabled:text-gray-400 disabled:cursor-not-allowed',
        small ? 'px-2 py-1.5' : 'px-4 py-2',
        variant in VARIANT_CLASSES ? VARIANT_CLASSES[variant] : undefined,
        props.className
    );
    const content = (
        <>
            {props.iconStart}
            {props.children}
            {props.iconEnd}
        </>
    );
    const baseProps = {
        'data-testid': props.dataTestid,
        'data-component-name': 'Button',
        className: classNames,
    };
    const { disabled } = props as ButtonTypes | ButtonSubmitTypes | LinkTypes | ExternalLinkTypes;

    if (disabled) {
        const { htmlAttributes } = props as ButtonTypes | ButtonSubmitTypes | LinkTypes | ExternalLinkTypes;
        return (
            <button
                disabled
                aria-disabled='true'
                {...baseProps}
                {...(htmlAttributes as HTMLAttributes<HTMLButtonElement>)}
            >
                {content}
            </button>
        );
    }
    if (type === 'submit' || type === 'button') {
        const { onClick, htmlAttributes } = props as ButtonTypes;
        return (
            <button
                type={type}
                onClick={onClick}
                {...baseProps}
                {...htmlAttributes}
            >
                {content}
            </button>
        );
    }
    if (type === 'link') {
        const { to, htmlAttributes } = props as LinkTypes;
        return (
            <Link
                to={to}
                {...baseProps}
                {...htmlAttributes}
            >
                {content}
            </Link>
        );
    }
    const { href, htmlAttributes } = props as ExternalLinkTypes;
    return (
        <a
            href={href}
            data-testid={props.dataTestid}
            className={classNames}
            target='_blank'
            rel='noopener noreferrer'
            {...htmlAttributes}
        >
            {content}
        </a>
    );
};
